import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Fullscreen as FullscreenIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { backendApiService } from '../../services/backendApiService';

interface QuerySnapshot {
  id: string;
  query: string;
  country: string;
  screenshot_path: string;
  thumbnail_path: string;
  ai_overview_detected: boolean;
  ai_overview_content: string;
  capture_date: string;
  capture_status: string;
  error_message: string | null;
  capture_duration_ms: number;
}

export const QuerySnapshotsViewer: React.FC = () => {
  const [snapshots, setSnapshots] = useState<QuerySnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchSnapshots();
  }, []);

  const fetchSnapshots = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await backendApiService.get('/api/ai-screenshots/query-snapshots');
      
      if (result.success && result.data) {
        setSnapshots(result.data);
      } else {
        setError(result.error || 'Failed to fetch snapshots');
      }
    } catch (err: any) {
      setError('Failed to fetch query snapshots');
      console.error('Error fetching snapshots:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteSnapshot = async (id: string) => {
    if (!confirm('Are you sure you want to delete this snapshot?')) {
      return;
    }

    setDeletingId(id);
    try {
      const result = await backendApiService.delete(`/api/ai-screenshots/${id}`);
      
      if (result.success) {
        setSnapshots(prev => prev.filter(s => s.id !== id));
      } else {
        setError('Failed to delete snapshot');
      }
    } catch (err) {
      setError('Failed to delete snapshot');
      console.error('Error deleting snapshot:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const getImageUrl = (screenshotId: string, thumbnail = false) => {
    return `/api/ai-screenshots-public/image/${screenshotId}?thumbnail=${thumbnail}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Button onClick={fetchSnapshots} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (snapshots.length === 0) {
    return (
      <Box textAlign="center" p={3}>
        <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="textSecondary" gutterBottom>
          No Query Snapshots Yet
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Capture screenshots of expanded queries by clicking the camera button next to each query.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''} captured from query expansions
      </Typography>

      <Grid container spacing={2}>
        {snapshots.map((snapshot) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={snapshot.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardMedia
                component="img"
                height="200"
                image={getImageUrl(snapshot.id, true)}
                alt={`Screenshot for: ${snapshot.query}`}
                sx={{ cursor: 'pointer' }}
                onClick={() => setFullscreenImage(getImageUrl(snapshot.id, false))}
              />
              <CardContent sx={{ flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom noWrap>
                  {snapshot.query}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                  {formatDate(snapshot.capture_date)}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {snapshot.ai_overview_detected ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="AI Overview"
                      size="small"
                      color="primary"
                    />
                  ) : (
                    <Chip
                      icon={<WarningIcon />}
                      label="No AI Overview"
                      size="small"
                      color="default"
                    />
                  )}
                  <Chip
                    icon={<ScheduleIcon />}
                    label={`${snapshot.capture_duration_ms}ms`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </CardContent>
              <CardActions>
                <Tooltip title="View fullscreen">
                  <IconButton
                    size="small"
                    onClick={() => setFullscreenImage(getImageUrl(snapshot.id, false))}
                  >
                    <FullscreenIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete snapshot">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => deleteSnapshot(snapshot.id)}
                    disabled={deletingId === snapshot.id}
                  >
                    {deletingId === snapshot.id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DeleteIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Fullscreen Dialog */}
      <Dialog
        open={!!fullscreenImage}
        onClose={() => setFullscreenImage(null)}
        maxWidth={false}
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Query Snapshot</Typography>
            <IconButton onClick={() => setFullscreenImage(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <img
            src={fullscreenImage || ''}
            alt="Fullscreen query snapshot"
            style={{ width: '100%', height: 'auto' }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};